/**
 * New task — `docs/activity.md` §9.5 (Board 11 E, 620) and its phone sheet
 * (§9.8, Board 16 E).
 *
 * *Task* · *Due* and a time (*a time puts it on the calendar*) · *Reminder* ·
 * **Assigned to** — one person or several, one tick finishes it for all
 * (§4.7) · *Repeats* · *Task list* · **Attached to** (optional — D20) ·
 * *Notes* with **Dictate**. With a link it is written on the first one
 * through the record composer's endpoint; with none, as a team item. A day
 * with no time is stored at noon, off the calendar (`dueHasTime: false`), as
 * the Task sheet stores it.
 *
 * Aaron, Oct 25: *Due* is a date, and the time is asked for only on purpose
 * (note 1.8 — **+ Add a time**, `DueControls`, the Task sheet's too: note
 * 1.7); *Notes* are rich text (note 1.4, `RichNoteEditor`).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  REMINDER_OPTIONS, TASK_PRIORITIES, TASK_PRIORITY_LABELS, TASK_REPEAT_OPTIONS, firstNameOf, taskFooterWords, zonedInstant,
  type RepeatRule, type TaskCategory, type TaskPriority,
} from '@rowted/shared'
import { Dictation } from '../../components/dictation.js'
import { RichNoteEditor, type RichNoteHandle } from '../../components/note-rich-editor.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { tasksChanged } from '../tasks/task-sheet.js'
import { PriorityGlyph } from '../tasks/task-priority.js'
import { TaskCategoriesField } from '../tasks/task-categories.js'
import { NewTaskFiles, type TaskFileView } from '../tasks/task-files.js'
import { AttachedTo, cameWithWords, type AttachChip } from './attach.js'
import { makeActivity, openedFromWords, problemOf, useContact, usePrefillChips, useSetup, type NewPrefill } from './common.js'
import { Field, Gap, MenuField, NewShell, PICKER_LAYER, Pair, TextBox } from './kit.js'
import { PeopleField, usePeopleLookup } from './people-field.js'
import { DueControls } from './when.js'

interface ListOption { id: string; name: string }

export function TaskSheet({
  prefill, phone, onClose, onMade,
}: { prefill: NewPrefill; phone: boolean; onClose: () => void; onMade: () => void }) {
  const { me, today } = useActivity()
  const setup = useSetup()
  const { byId } = usePeopleLookup()
  const timeZone = me.user.timeZone
  const [title, setTitle] = useState(prefill.title ?? '')
  const [date, setDate] = useState<string | null>(prefill.date ?? null)
  const [time, setTime] = useState<number | null>(prefill.start ?? null)
  /* A time only on purpose (note 1.8): open when the calendar asked for one, closed otherwise. */
  const [timeOn, setTimeOn] = useState(prefill.start != null)
  const [reminder, setReminder] = useState<number | null>(15)
  const [people, setPeople] = useState<string[]>(prefill.people?.length ? prefill.people : [me.user.id])
  const [repeat, setRepeat] = useState<RepeatRule | null>(null)
  const [listId, setListId] = useState<string | null>(prefill.taskListId ?? null)
  const [lists, setLists] = useState<ListOption[]>([])
  const [chips, setChips] = usePrefillChips(prefill)
  const [picked, setPicked] = useState<{ chip: AttachChip; brought: AttachChip[] } | null>(null)
  const [notes, setNotes] = useState('')
  /* Planner's priority and categories (Aaron, Oct 25, note 1.5 — E2). A checklist is added once it exists. */
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [categories, setCategories] = useState<TaskCategory[]>([])
  /* Its attachments (Aaron, Oct 26): uploaded as they are picked, linked by the create. */
  const [files, setFiles] = useState<TaskFileView[]>([])
  const noteBox = useRef<RichNoteHandle | null>(null)
  const onNoteBox = useCallback((handle: RichNoteHandle | null) => { noteBox.current = handle }, [])
  /* Dictate's words go in where the box is; before the box has loaded, onto the end of the note. */
  const dictate = (said: string) => {
    if (noteBox.current) noteBox.current.insertText(said)
    else setNotes((was) => (was ? `${was} ${said}` : said))
  }
  const minutes = date && timeOn ? time : null
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const threadContact = useContact(prefill.fromConversationId ? prefill.contactId ?? null : null)

  useEffect(() => {
    let live = true
    activityRequest<{ items: ListOption[] }>('/task-lists')
      .then((answer) => { if (live) setLists(answer.items.map((item) => ({ id: item.id, name: item.name }))) })
      .catch(() => {})
    return () => { live = false }
  }, [])

  const firstNames = people.map((id) => byId(id)?.name ?? (id === me.user.id ? me.user.name : '')).map(firstNameOf)
  const hint = cameWithWords(picked)
    ?? openedFromWords({ name: threadContact?.name ?? null, kind: 'thread', required: false, phone })

  const create = async () => {
    if (!title.trim()) { setProblem('Give the task a name.'); return }
    if (repeat && !date) { setProblem('A task that repeats needs a due date.'); return }
    setBusy(true)
    setProblem(null)
    const body = {
      kind: 'task',
      title: title.trim(),
      body: notes.trim() || undefined,
      dueAt: date ? zonedInstant(date, minutes ?? 12 * 60, timeZone).toISOString() : null,
      dueHasTime: date ? minutes !== null : undefined,
      reminderMinutes: date ? reminder : null,
      repeatRule: repeat,
      participantIds: people,
      taskListId: listId,
      bucketId: listId && listId === prefill.taskListId ? prefill.bucketId ?? null : null,
      visibility: 'internal',
      ...(priority !== 'medium' ? { priority } : {}),
      ...(categories.length > 0 ? { categoryIds: categories.map((category) => category.id) } : {}),
      ...(files.length > 0 ? { fileIds: files.map((file) => file.id) } : {}),
    }
    try {
      await makeActivity(body, chips)
      tasksChanged()
      onMade()
      onClose()
    } catch (cause) {
      setProblem(problemOf(cause))
    } finally {
      setBusy(false)
    }
  }

  const titleField = (
    <Field label="Task" required phone={phone}>
      <TextBox label="Task" value={title} onChange={setTitle} phone={phone} autoFocus bold={!phone} onEnter={() => void create()} />
    </Field>
  )
  const reminderField = (
    <Field label="Reminder" phone={phone}>
      <MenuField label="Reminder" phone={phone} value={reminder === null ? 'none' : String(reminder)}
        options={REMINDER_OPTIONS.map((option) => ({ value: option.minutes === null ? 'none' : String(option.minutes), label: option.label }))}
        onChange={(next) => setReminder(next === 'none' ? null : Number(next))} />
    </Field>
  )
  const repeatField = (
    <Field label="Repeats" phone={phone}>
      <MenuField label="Repeats" phone={phone} value={repeat ?? 'never'}
        options={TASK_REPEAT_OPTIONS.map((option) => ({ value: option.rule ?? 'never', label: option.label }))}
        onChange={(next) => setRepeat(next === 'never' ? null : next as RepeatRule)} />
    </Field>
  )
  const priorityField = (
    <Field label="Priority" phone={phone}>
      <MenuField label="Priority" phone={phone} value={priority}
        options={TASK_PRIORITIES.map((value) => ({ value, label: TASK_PRIORITY_LABELS[value], lead: <PriorityGlyph priority={value} /> }))}
        onChange={setPriority} />
    </Field>
  )
  const categoriesField = (
    <Field label="Categories" optional phone={phone}>
      <TaskCategoriesField value={categories} onChange={setCategories} height={phone ? 46 : 36} listId={listId} />
    </Field>
  )
  const attachField = (
    <AttachedTo chips={chips} required={false} phone={phone} hint={hint} id="new-task-attached"
      onChange={(next, pick) => { setChips(next); setPicked(pick ?? null) }} />
  )
  const notesField = (
    <Field label="Notes" optional phone={phone}>
      <RichNoteEditor label="Notes" value={notes} onChange={setNotes} phone={phone} layer={PICKER_LAYER}
        /* Board 11 E's 46 of words and 16 E's 92 drawn, padding included. */
        minHeight={phone ? 92 : 66} placeholder={phone ? 'Add a note' : undefined} onReady={onNoteBox}
        tools={phone
          ? <span className="text-accent"><Dictation variant="icon" onText={dictate} /></span>
          : <Dictation variant="chip" onText={dictate} />} />
    </Field>
  )
  const filesField = (
    <Field label="Attachments" optional phone={phone}>
      <NewTaskFiles files={files} onChange={setFiles} />
    </Field>
  )
  const dueField = (words: string) => (
    <DueControls date={date} time={time} timeOn={timeOn} phone={phone} today={today} words={words}
      onDate={setDate} onClear={() => { setDate(null); setTimeOn(false) }} onTime={setTime} onTimeOn={setTimeOn} />
  )

  if (phone) {
    const fromList = Boolean(prefill.taskListId)
    return (
      <NewShell title="New task" sub={setup?.sub ?? ''} look="task" width={620} phone note={null}
        primary="Create task" phoneAction="Create" onPrimary={() => void create()} busy={busy}
        dirty={title.trim().length > 0 || notes.trim().length > 0 || files.length > 0} onClose={onClose} problem={problem}>
        {titleField}
        <Gap />
        <Field label="Due" phone>
          {dueField('a time puts it on your calendar')}
        </Field>
        <Gap />
        {attachField}
        <Gap />
        <PeopleField label="Assigned to" people={people} onChange={setPeople} phone meWord />
        <Gap />
        <Pair phone>{reminderField}{repeatField}</Pair>
        <Gap />
        {priorityField}
        <Gap />
        {categoriesField}
        {fromList && (
          <>
            <Gap />
            <Field label="Task list" phone>
              <MenuField label="Task list" phone value={listId} options={lists.map((list) => ({ value: list.id, label: list.name }))}
                onChange={setListId} />
            </Field>
          </>
        )}
        <Gap />
        {notesField}
        <Gap />
        {filesField}
      </NewShell>
    )
  }

  return (
    <NewShell title="New task" sub={setup?.sub ?? ''} look="task" width={620} phone={false}
      note={taskFooterWords({ firstNames, date, minutes })}
      primary="Create task" phoneAction="Create" onPrimary={() => void create()} busy={busy}
      dirty={title.trim().length > 0 || notes.trim().length > 0 || files.length > 0} onClose={onClose} problem={problem}>
      {titleField}
      <Gap />
      <Pair>
        <Field label="Due">
          {dueField('a time puts it on the calendar')}
        </Field>
        {reminderField}
      </Pair>
      <Gap />
      <PeopleField label="Assigned to" people={people} onChange={setPeople} phone={false}
        words="one person or several — one tick finishes it for all" />
      <Gap />
      <Pair>
        {repeatField}
        <Field label="Task list" optional>
          <MenuField label="Task list" phone={false} value={listId ?? 'none'} placeholder="None"
            options={[{ value: 'none', label: 'None' }, ...lists.map((list) => ({ value: list.id, label: list.name }))]}
            onChange={(next) => setListId(next === 'none' ? null : next)}
            display={listId ? undefined : <span className="truncate text-text-faint">None</span>} />
        </Field>
      </Pair>
      <Gap />
      <Pair>{priorityField}{categoriesField}</Pair>
      <Gap />
      {attachField}
      <Gap />
      {notesField}
      <Gap />
      {filesField}
      {repeat && !date && (
        <div className="mt-2 flex items-center gap-1.5 text-[12px] text-warn"><Glyph name="info" size={13} />Pick a due date for a task that repeats.</div>
      )}
    </NewShell>
  )
}
